/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { ServiceRequest } from '@src/app/model/serviceRequest';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { DialogBannerComponent } from '@src/app/shared/dialogs/dialog-banner-component';
import { FieldSet, FieldSetComponent } from '@src/app/shared/form/field-set/field-set.component';
import { FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { FormRichTextComponent } from '@src/app/shared/form/form-rich-text/form-rich-text.component';
import { CloseRequestData, RequestService } from '@src/app/modules/crm/request.service';
import { MatButtonModule } from '@angular/material/button';
import { AbstractObject } from '@src/app/model/abstract-object';
import { MessageService } from '@src/app/shared/message.service';
import { ControlVisibility, Field } from '@src/app/shared/field/Field';
import { HttpParams } from '@angular/common/http';
import { FormComboBoxComponent } from '@src/app/shared/form/form-combo-box/form-combo-box.component';
import { CommsTemplateType } from '@src/app/model/CommsTemplate';
import { FormDurationComponent } from '@src/app/shared/form/form-duration/form-duration.component';
import { IsNarrowService } from '@src/app/shared/is-narrow.service';
import { forkJoin, Subscription } from 'rxjs';
import { SrTopic } from '@src/app/model/sr-topic';
import { SrClass } from '@src/app/model/sr-class';
import { SrTopicService } from '@src/app/modules/crm/sr-topic.service';
import { SrClassService } from '@src/app/modules/crm/sr-class.service';

@Component({
    selector: 'app-resolve-request',
    imports: [MatDialogModule, FieldSetComponent, MatButtonModule, DialogBannerComponent],
    templateUrl: './resolve-request.component.html',
    styleUrl: './resolve-request.component.scss'
})
export class ResolveRequestComponent implements OnDestroy {
    dialogRef = inject(MatDialogRef<ResolveRequestComponent>);
    sr = inject(MAT_DIALOG_DATA);
    srSvc = inject(RequestService);
    msgSvc = inject(MessageService);
    topicSvc = inject(SrTopicService);
    classSvc = inject(SrClassService);
    isPhone = false;
    screenSvc = inject(IsNarrowService);
    subscriptions: Subscription[] = [];

    defaultComment = $localize `If you include comments above about what was done,
        they will be added as a comment on the request and
        included in the notification to followers that request has been closed.`;
    comment = this.defaultComment;

    ready = false;
    private classes: SrClass[] = [];

    fieldSet = new FieldSet({ formLayout: [{ cells: [{ width: '33%' }, { width: '66%' }] }]});
    closeStatusField = FormPicklistComponent.make('Close Status', 'srCloseStatus', null,
        { items: ServiceRequest.srCloseStates, refreshes: [this.closeStatusRefresh.bind(this)] }
    )

    duplicateRequestField = FormComboBoxComponent.make('Duplicate Of Request', 'duplicateId', null, {
        items: [],
        optionDisplayValue: sr => sr ? sr.refNr + ': ' + sr.title : '',
        refreshes: [this.duplicateSrRefresh.bind(this)]
    }, {visible: Field.noShow});

    closeTextField = FormRichTextComponent.make('What was done?', 'closeText', {formColumn:2}).setCommsTemplate(CommsTemplateType.REQUEST_CLOSING);

    closeWorkField = FormDurationComponent.make('Time to complete', 'closeWorkTime', {});

    requestIdField = FieldMaker.idHolder('requestId');

    // Confirmed rather than merely displayed: these two are the analysis the closed request feeds,
    // and they replace the category question that used to gate the close.
    topicClass = ServiceRequest.makeTopicAndClassFields(() => this.classes);

    fields = [
        this.requestIdField,
        this.closeStatusField,
        this.duplicateRequestField,
        this.closeTextField,
        this.closeWorkField,
        this.topicClass.topicField.override({ formColumn: 1 }),
        this.topicClass.classField.override({ formColumn: 1 }),
    ]

    constructor() {
        this.subscriptions.push(this.screenSvc.detectVeryNarrow().subscribe(result => { this.isPhone = result }));
        this.fieldSet.setFields(this.fields);
        this.requestIdField.setValue({requestId: this.sr.id} as unknown as AbstractObject);
        this.closeStatusField.setValue({srCloseStatus: ServiceRequest.srComplete.id} as unknown as AbstractObject);
        this.closeWorkField.setValue({closeWorkTime: 15} as unknown as AbstractObject);
        this.loadTaxonomy();
    }

    /** data-form-ready only goes up once the topic and class lists are in - see client/CLAUDE.md. */
    private loadTaxonomy() {
        forkJoin({
            topics: this.topicSvc.get<SrTopic>(false),
            classes: this.classSvc.get<SrClass>(false),
        }).subscribe(r => {
            this.classes = r.classes;
            this.topicClass.topicField.setPicklistItems(r.topics);
            this.topicClass.narrowClasses(this.sr.srTopic);
            this.topicClass.topicField.setValue({ srTopic: this.sr.srTopic } as unknown as AbstractObject);
            this.topicClass.classField.setValue({ srClass: this.sr.srClass } as unknown as AbstractObject);
            this.ready = true;
        });
    }

    highlightErrors() {
        this.fields.forEach( f => {
            if (!f.control?.valid) {
                f.control.markAsTouched()
                console.log('Invalid ', f);
            }
        });
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
        this.subscriptions = [];
    }

    closeStatusRefresh(closeStatus: AbstractObject) {
        let commentVisibility: ControlVisibility = Field.showAll;
        this.comment = this.defaultComment

        if (closeStatus.id === ServiceRequest.srDuplicate.id) {
            this.duplicateRequestField.visible = Field.showAll;
            const parms = new HttpParams().set('srStatus', ServiceRequest.srOpen.id);
            this.srSvc.get(false, parms, this.sr.teamId).subscribe( openSRs => {
                this.duplicateRequestField.setPicklistItems(openSRs.filter( sr => sr.id !== this.sr.id));
            })
        } else {
            this.duplicateRequestField.visible = Field.noShow;
            this.duplicateRequestField.clearComboValue();
            this.duplicateRequestField.control.setValue(null, {emitEvent: false});
            if (closeStatus.id === ServiceRequest.srCancelled.id) {
                const msg = $localize `<p>This request is being close because we understand it is no longer required,
                    or no further action is required</p>`;
                this.closeTextField.control.setValue(msg);
            } else if (closeStatus.id === ServiceRequest.srNoAction.id) {
                this.comment = $localize `This request is being close because no action is required.
                There will be NO COMMENT STORED, NO NOTIFICATION SENT TO Reporter.
                Status will go directly to accepted. It will not be possible to re-open the request.
                Only use this option if you are certain that there is no work required related to this request
                and that the person who created the request has no desire to receive further information about this request.`;
                commentVisibility = Field.noShow;
                this.closeTextField.control.setValue('');
            } else {
                this.closeTextField.control.setValue('');
            }
        }
        this.closeTextField.visible = commentVisibility
    }

    duplicateSrRefresh(sr: ServiceRequest){
        let msg = '';
        if (sr.id) {
            msg = $localize `<p>This request ${this.sr.refNr} is a duplicate of ${sr.refNr}</p>
            <p>We are marking ${this.sr.refNr} closed, but we will continue working on request ${sr.refNr}</p>
            <p>You have been listed as a follower of ${sr.refNr} and will receive updates on the issue as it progresses</p>`
        }
        this.closeTextField.control.setValue(msg);
    }

    closeRequest() {
        // teamId is not a field on the dialog, and closeRequest addresses the write with it - see
        // RequestService. Without it the close runs in whatever team the page is on, which is the
        // agency when the request was opened from the agency's own list.
        const data = { ...this.fieldSet.getFormValue(), teamId: this.sr.teamId } as unknown as CloseRequestData;
        this.srSvc.closeRequest(data).subscribe ( response => {
            if (response) {
                this.dialogRef.close(response);
            }
        }, error => this.msgSvc.showError(error, 'Resolving Request'));
    }
}
